package com.spground.spaceplaygroundwallpaper

import kotlin.math.*
import kotlin.random.Random

/**
 * Web port of the Android SpaceSceneEngine (app/src/main/java/.../SpaceSceneEngine.kt).
 *
 * The scene/physics/draw logic is kept line-for-line identical to the Android
 * source on purpose — when the Android engine changes, re-porting is a
 * mechanical diff. Only the platform seams differ:
 *   - android.graphics.*  -> Graphics.kt shim over Canvas2D
 *   - SharedPreferences   -> WallpaperSettings (localStorage)
 *   - BitmapFactory/R.*   -> Assets (HTMLImageElement sprites)
 *   - displayMetrics.density -> density passed by the caller (devicePixelRatio)
 *   - PowerManager battery saver -> dropped (low-power pref only)
 */
class SpaceSceneEngine(private val density: Float) {

    // -- Paint state (reused across draw calls) --
    private val paint = Paint(Paint.ANTI_ALIAS_FLAG)
    private val rocketMatrix = Matrix()
    private val debrisMatrix = Matrix()
    private val planetMatrix = Matrix()

    // Reused to avoid per-frame Path allocations — each usage calls reset() first.
    private val bodyPath = Path()

    // -- Scene state --
    private val stars = mutableListOf<Star>()
    private val shootingStars = mutableListOf<ShootingStar>()
    private val spiralGalaxies = mutableListOf<SpiralGalaxy>()
    private val rockets = mutableListOf<EngineRocket>()
    private val particles = mutableListOf<PropulsionParticle>()
    private val debris = mutableListOf<Debris>()
    private val gravityWells = mutableListOf<GravityWell>()
    private var lastRocketSpawnTime = 0f
    private var globalTime = 0f

    // -- Warp animation --
    private var warping = false
    private var warpElapsed = 0f
    private val warpDuration: Float
        get() = prefInt(WallpaperSettings.KEY_WARP_DURATION, WallpaperSettings.DEF_WARP_DURATION) / 10f

    // -- Size / init --
    private var screenW = 0f
    private var screenH = 0f
    private var initialized = false
    private val refSize: Float
        get() {
            val maxPx = density * 420f
            return min(screenW, screenH).coerceAtMost(maxPx)
        }

    // -- Bitmaps (loaded once) --
    private val rocketBitmaps: Map<RocketType, Bitmap> by lazy {
        RocketType.entries.associateWith { Assets.bitmap(it.asset) }
    }
    private val debrisBitmaps: Map<RocketType, List<Bitmap>> by lazy {
        RocketType.entries.associateWith { t ->
            t.debrisAssets.map { Assets.bitmap(it) }
        }
    }
    private val planetBitmaps: List<Bitmap> by lazy {
        listOf("planet1", "planet2", "planet3").map { Assets.bitmap(it) }
    }
    private val cloudBitmaps: List<Bitmap> by lazy {
        listOf("clouds1", "clouds2", "clouds3").map { Assets.bitmap(it) }
    }

    // Cached color filters, same as Android (there they wrap native objects; here
    // they key the tinted-canvas cache in the shim).
    private val rocketOuterFilter: Map<RocketType, PorterDuffColorFilter> by lazy {
        RocketType.entries.associateWith {
            PorterDuffColorFilter(brightenColor(it.color, 0.9f), PorterDuff.Mode.SRC_IN)
        }
    }
    private val rocketMidFilter: Map<RocketType, PorterDuffColorFilter> by lazy {
        RocketType.entries.associateWith {
            PorterDuffColorFilter(brightenColor(it.color, 0.7f), PorterDuff.Mode.SRC_IN)
        }
    }
    private val rocketGlintFilter: Map<RocketType, PorterDuffColorFilter> by lazy {
        RocketType.entries.associateWith {
            PorterDuffColorFilter(withOpacity(brightenColor(it.color, 0.1f), 0.1f), PorterDuff.Mode.SRC_IN)
        }
    }
    private val debrisTintFilter: Map<RocketType, PorterDuffColorFilter> by lazy {
        RocketType.entries.associateWith {
            PorterDuffColorFilter(brightenColor(it.color, 0.8f), PorterDuff.Mode.SRC_ATOP)
        }
    }

    // Canonical white star sparkle, pre-rendered once; stars are drawn with
    // drawBitmap + transform + colorFilter.
    private val starBitmapSize = 256
    private val starBitmap: Bitmap by lazy {
        val bmp = Bitmap.createBitmap(starBitmapSize, starBitmapSize, Bitmap.Config.ARGB_8888)
        val c = Canvas(bmp)
        val p = Paint(Paint.ANTI_ALIAS_FLAG).apply {
            style = Paint.Style.FILL
            color = Color.WHITE
        }
        val half = starBitmapSize / 2f
        val path = Path()
        buildStarPath(path, half, half, half * 0.95f)
        c.drawPath(path, p)
        bmp
    }
    private val starGlowFilter: HashMap<Int, PorterDuffColorFilter> by lazy {
        val m = HashMap<Int, PorterDuffColorFilter>()
        for (c in SPACE_COLORS) {
            m[c] = PorterDuffColorFilter(brightenColor(c, 0.9f), PorterDuff.Mode.SRC_IN)
        }
        m
    }

    // Per-warp-frame glow filters (colors lerp toward white as warpFactor rises).
    private val warpGlowFilter: HashMap<Int, PorterDuffColorFilter> = HashMap()
    private var warpGlowFactor = -1f

    // Single cached DashPathEffect for orbit lines; the phase is 0 and we rotate
    // the canvas instead (same trick as Android).
    private val orbitDash = DashPathEffect(floatArrayOf(15f, 20f), 0f)

    // Viewport offset — scene draws get translated by this (parallax/pointer
    // tilt, set from the caller). Overlays (scrim/glass) stay in raw screen space.
    private var viewportOffsetX = 0f
    private var viewportOffsetY = 0f

    /** Padding around the visible screen where scene content is allowed to spawn. */
    private val scenePadX: Float get() = screenW * 0.15f
    private val scenePadY: Float get() = screenH * 0.15f

    // Where moving bodies enter the scene from. Must clear the largest viewport
    // translate the caller can apply by a comfortable margin.
    private val spawnMarginX: Float get() = screenW * 0.40f
    private val spawnMarginY: Float get() = screenH * 0.40f

    // -- Public API --

    /** Shift the scene by (x, y) pixels for this frame. Overlays are unaffected. */
    fun setViewportOffset(x: Float, y: Float) {
        viewportOffsetX = x
        viewportOffsetY = y
    }

    /** Set or update the render surface size. Re-initializes the scene if size changed. */
    fun setSize(w: Float, h: Float): SizeChange {
        val oldW = screenW
        val oldH = screenH
        screenW = w
        screenH = h
        val needsInit = !initialized || oldW != w || oldH != h
        if (needsInit) initScene()
        return when {
            !initialized -> SizeChange.INITIAL
            oldW == 0f && oldH == 0f -> SizeChange.INITIAL
            oldW != w || oldH != h -> SizeChange.RESIZED
            else -> SizeChange.UNCHANGED
        }
    }

    /** Force a fresh scene on next draw. */
    fun reset() {
        initialized = false
    }

    /** Play the warp-speed animation and clear transient bodies. */
    fun triggerWarp() {
        warping = true
        warpElapsed = 0f
        rockets.clear()
        debris.clear()
        particles.clear()
        lastRocketSpawnTime = globalTime
        if (screenW > 0f && screenH > 0f) initGravityWells()
    }

    /** Current on-screen counts for frame-timing diagnostics. */
    fun rocketCount(): Int = rockets.size
    fun debrisCount(): Int = debris.size
    fun particleCount(): Int = particles.size
    fun starCount(): Int = stars.size

    /** True if the low-power gate is on (user pref; no battery-saver signal on web). */
    fun isLowPowerActive(): Boolean {
        return prefBool(WallpaperSettings.KEY_LOW_POWER, WallpaperSettings.DEF_LOW_POWER)
    }

    /**
     * Advance physics and render in one pass (the scene code interleaves
     * update+draw by design, so a single entry point keeps behavior identical).
     */
    fun updateAndDraw(canvas: Canvas, dt: Float) {
        if (screenW <= 0f || screenH <= 0f) return
        if (!initialized) initScene()

        globalTime += dt
        if (warping) {
            warpElapsed += dt
            if (warpElapsed >= warpDuration) {
                warping = false
                warpElapsed = 0f
                // Warp just finished: reset the grow-in spawnTimes for planets
                // and galaxies so they scale in from zero *after* the warp.
                for (well in gravityWells) well.spawnTime = globalTime
                for (galaxy in spiralGalaxies) galaxy.spawnTime = globalTime
                lastRocketSpawnTime = globalTime
            }
        }

        val lowPower = isLowPowerActive()

        // Background — fills the whole surface before any offset.
        val trueBlack = prefBool(WallpaperSettings.KEY_TRUE_BLACK, WallpaperSettings.DEF_TRUE_BLACK)
        canvas.drawColor(if (trueBlack) Color.BLACK else BG_COLOR)

        // Translate for parallax. Scene draws inside this transform; we restore
        // before overlays so scrim + glass don't slide.
        val hasOffset = viewportOffsetX != 0f || viewportOffsetY != 0f
        if (hasOffset) {
            canvas.save()
            canvas.translate(viewportOffsetX, viewportOffsetY)
        }

        val warpRatio = if (warping) (warpElapsed / warpDuration).coerceIn(0f, 1f) else 0f
        val warpIntensity = prefInt(WallpaperSettings.KEY_WARP_INTENSITY, WallpaperSettings.DEF_WARP_INTENSITY) / 100f
        val warpFactor = sin(PI.toFloat() * warpRatio).coerceAtLeast(0f) * warpIntensity

        // Dashed gravity-field borders
        val showGravityField = prefBool(WallpaperSettings.KEY_SHOW_GRAVITY_FIELD, WallpaperSettings.DEF_SHOW_GRAVITY_FIELD)
        val borderThickness = prefInt(WallpaperSettings.KEY_GRAVITY_BORDER_THICKNESS, WallpaperSettings.DEF_GRAVITY_BORDER_THICKNESS) / 10f
        val borderBrightness = prefInt(WallpaperSettings.KEY_GRAVITY_BORDER_BRIGHTNESS, WallpaperSettings.DEF_GRAVITY_BORDER_BRIGHTNESS) / 100f
        if (!warping && showGravityField) {
            paint.pathEffect = orbitDash
            paint.style = Paint.Style.STROKE
            paint.strokeWidth = borderThickness
            for (well in gravityWells) {
                val scale = growScale(well.spawnTime, well.enterDuration)
                val r = well.gravityRadius * scale

                paint.color = withOpacity(Color.WHITE, borderBrightness * scale)
                // Rotate the canvas by a phase angle so the dashes appear to
                // travel around the ring.
                val phaseDeg = (globalTime * 30f / r) * (180f / PI.toFloat())
                canvas.save()
                canvas.translate(well.x + wobbleDx(well), well.y + wobbleDy(well))
                canvas.rotate(phaseDeg)
                canvas.drawCircle(0f, 0f, r, paint)
                canvas.restore()
            }
            paint.pathEffect = null
        }

        // Stars (not dimmed — small backdrop)
        val scrollMul = prefInt(WallpaperSettings.KEY_STAR_SCROLL, WallpaperSettings.DEF_STAR_SCROLL) / 100f
        // During warp, rebuild the tinted-white glow filters (one per space color).
        if (warpFactor > 0.01f && warpFactor != warpGlowFactor) {
            warpGlowFilter.clear()
            for (c in SPACE_COLORS) {
                val lerped = lerpColor(c, Color.WHITE, warpFactor)
                warpGlowFilter[c] = PorterDuffColorFilter(
                    brightenColor(lerped, 0.9f), PorterDuff.Mode.SRC_IN
                )
            }
            warpGlowFactor = warpFactor
        } else if (warpFactor <= 0.01f && warpGlowFactor != -1f) {
            warpGlowFilter.clear()
            warpGlowFactor = -1f
        }
        val warpActive = warping && warpFactor > 0.01f
        val cx = screenW / 2f
        val cy = screenH / 2f
        val starPadX = scenePadX
        val starPadY = scenePadY
        val starSpawnW = screenW + 2f * starPadX
        val starSpawnH = screenH + 2f * starPadY
        for (star in stars) {
            if (warpActive) {
                // Real warp: stars fly radially outward from screen center,
                // velocity proportional to distance.
                val dx = star.x - cx
                val dy = star.y - cy
                val k = 1.4f * warpFactor
                star.x += dx * k * dt
                star.y += dy * k * dt
                if (star.x < -starPadX || star.x > screenW + starPadX ||
                    star.y < -starPadY || star.y > screenH + starPadY) {
                    star.x = -starPadX + Random.nextFloat() * starSpawnW
                    star.y = -starPadY + Random.nextFloat() * starSpawnH
                }
            } else {
                star.y += star.velocityY * dt * scrollMul
                // Wrap across the padded band the stars were spawned in, not the
                // bare screen: parallax shifts the viewport into that padding, so
                // wrapping at screenH popped stars out and back in on-screen.
                if (star.y > screenH + starPadY + star.radius * 2f) star.y -= starSpawnH
            }
            drawStar(canvas, star, warpFactor)
        }

        // Shooting stars
        if (!warping) {
            for (ss in shootingStars) updateAndDrawShootingStar(canvas, ss, dt)
        }

        // Element dimming layer — reduce opacity of large elements. At 100%
        // there's nothing to dim, so skip the offscreen layer entirely.
        val elementDim = prefInt(WallpaperSettings.KEY_ELEMENT_DIM, WallpaperSettings.DEF_ELEMENT_DIM) / 100f
        val dimAlpha = (elementDim * 255f).toInt().coerceIn(0, 255)
        val dimmed = dimAlpha < 255
        if (dimmed) {
            // Describe the visible region in scene space: exactly the display,
            // un-translated (see the Android source for the full clipping saga).
            canvas.saveLayerAlpha(
                -viewportOffsetX, -viewportOffsetY,
                screenW - viewportOffsetX, screenH - viewportOffsetY,
                dimAlpha,
            )
        }

        // Spiral galaxies
        for (galaxy in spiralGalaxies) {
            galaxy.centerY += galaxy.velocityY * dt
            if (galaxy.centerY > screenH + galaxy.galaxyRadius) {
                galaxy.centerY = -4f * galaxy.galaxyRadius
                galaxy.centerX = screenW * Random.nextFloat()
            }
            galaxy.angle = globalTime / 10.5f
            if (!warping) drawSpiralGalaxy(canvas, galaxy)
        }

        // Spawn rockets — only once the warp intro is over.
        if (!warping) {
            val timeSinceLastRocket = globalTime - lastRocketSpawnTime
            val spawnInterval = prefInt(WallpaperSettings.KEY_ROCKET_SPAWN_INTERVAL, WallpaperSettings.DEF_ROCKET_SPAWN_INTERVAL) / 10f
            if (timeSinceLastRocket >= spawnInterval) {
                spawnRocket()
                lastRocketSpawnTime = globalTime
            }
        }

        // Propulsion particles (update)
        val particleLifetime = prefInt(WallpaperSettings.KEY_PARTICLE_LIFETIME, WallpaperSettings.DEF_PARTICLE_LIFETIME) / 10f
        val pIter = particles.iterator()
        while (pIter.hasNext()) {
            val p = pIter.next()
            p.elapsed += dt
            p.x += p.vx * dt
            p.y += p.vy * dt
            p.radius *= (1f - 1f * dt).coerceAtLeast(0.5f)
            if (p.elapsed >= particleLifetime) pIter.remove()
        }

        // Rockets (physics + planet overlap transitions)
        val planetCollision = prefBool(WallpaperSettings.KEY_PLANET_COLLISION, WallpaperSettings.DEF_PLANET_COLLISION)
        val particleChance = if (lowPower) 0.35f else 0.7f
        val rIter = rockets.iterator()
        while (rIter.hasNext()) {
            val rocket = rIter.next()
            if (!rocket.alive) { rIter.remove(); continue }
            rocket.elapsed += dt
            applyGravityToBody(rocket.x, rocket.y, rocket.radius, 2f) { ax, ay ->
                rocket.vx += ax * dt; rocket.vy += ay * dt
            }
            rocket.x += rocket.vx * dt; rocket.y += rocket.vy * dt
            if (Random.nextFloat() < particleChance) spawnPropulsionParticle(rocket)

            val margin = screenW * 1.2f
            if (rocket.x < -margin || rocket.x > screenW + margin ||
                rocket.y < -margin || rocket.y > screenH + margin) {
                rIter.remove(); continue
            }

            var hitPlanet = false
            for ((wi, well) in gravityWells.withIndex()) {
                val scale = growScale(well.spawnTime, well.enterDuration)
                val pr = well.planetRadius * scale
                val dx = rocket.x - (well.x + wobbleDx(well))
                val dy = rocket.y - (well.y + wobbleDy(well))
                val dist = sqrt(dx * dx + dy * dy)
                val overlapping = dist < pr + rocket.radius
                val wasOverlapping = wi in rocket.wasOverlappingCore
                if (overlapping && !wasOverlapping) {
                    if (planetCollision) {
                        spawnDebrisFromPlanetHit(rocket, well, lowPower)
                        rocket.alive = false; hitPlanet = true; break
                    } else {
                        if (wi in rocket.behindWells) rocket.behindWells.remove(wi)
                        else rocket.behindWells.add(wi)
                    }
                    rocket.wasOverlappingCore.add(wi)
                } else if (!overlapping && wasOverlapping) {
                    rocket.wasOverlappingCore.remove(wi)
                }
            }
            if (hitPlanet) { rIter.remove(); continue }
        }

        // Draw particles
        for (p in particles) drawPropulsionParticle(canvas, p, particleLifetime)

        // Rockets behind planets
        for (rocket in rockets) {
            if (rocket.alive && rocket.behindWells.isNotEmpty()) drawRocket(canvas, rocket)
        }

        // Planets
        val showPlanets = prefBool(WallpaperSettings.KEY_SHOW_PLANETS, WallpaperSettings.DEF_SHOW_PLANETS)
        if (!warping && showPlanets) {
            for (well in gravityWells) drawPlanet(canvas, well)
        }

        // Rockets in front of planets
        for (rocket in rockets) {
            if (rocket.alive && rocket.behindWells.isEmpty()) drawRocket(canvas, rocket)
        }

        // Rocket-rocket collisions
        checkRocketCollisions(lowPower)

        // Debris
        val debrisLifetime = prefInt(WallpaperSettings.KEY_DEBRIS_LIFETIME, WallpaperSettings.DEF_DEBRIS_LIFETIME) / 10f
        val spinMul = prefInt(WallpaperSettings.KEY_DEBRIS_SPIN, WallpaperSettings.DEF_DEBRIS_SPIN) / 100f
        val dIter = debris.iterator()
        while (dIter.hasNext()) {
            val d = dIter.next()
            d.elapsed += dt
            applyGravityToBody(d.x, d.y, d.radius, 1f) { ax, ay ->
                d.vx += ax * dt; d.vy += ay * dt
            }
            d.x += d.vx * dt; d.y += d.vy * dt
            d.angle += d.rotationSpeed * dt * spinMul
            val margin = screenW * 1.2f
            if (d.x < -margin || d.x > screenW + margin ||
                d.y < -margin || d.y > screenH + margin) {
                dIter.remove()
            } else {
                drawDebris(canvas, d, debrisLifetime)
            }
        }

        // End element-dim layer (only if we actually opened one)
        if (dimmed) canvas.restore()

        // Undo viewport translate before overlays so scrim/glass cover the full
        // screen regardless of parallax offset.
        if (hasOffset) canvas.restore()

        // Dark scrim
        val darkScrim = prefInt(WallpaperSettings.KEY_DARK_SCRIM, WallpaperSettings.DEF_DARK_SCRIM) / 100f
        if (darkScrim > 0f) {
            paint.style = Paint.Style.FILL
            paint.shader = null
            paint.color = withOpacity(Color.BLACK, darkScrim)
            canvas.drawRect(0f, 0f, screenW, screenH, paint)
        }

        // Glass overlay — gradient tint
        val glassPref = prefInt(WallpaperSettings.KEY_GLASS_TINT, WallpaperSettings.DEF_GLASS_TINT) / 100f
        if (glassPref > 0f) {
            val glassA = prefInt(WallpaperSettings.KEY_GLASS_ALPHA, WallpaperSettings.DEF_GLASS_ALPHA) / 255f
            val tintColor = WallpaperSettings.resolveGlassColor()
            val gradStrength = prefInt(WallpaperSettings.KEY_GLASS_GRADIENT, WallpaperSettings.DEF_GLASS_GRADIENT) / 100f
            val midFactor = 1f - gradStrength * 0.85f
            val edgeColor = withOpacity(tintColor, glassPref * glassA)
            val midColor = withOpacity(tintColor, glassPref * glassA * midFactor)
            paint.style = Paint.Style.FILL
            paint.shader = LinearGradient(
                0f, 0f, 0f, screenH,
                intArrayOf(edgeColor, midColor, midColor, edgeColor),
                floatArrayOf(0f, 0.35f, 0.65f, 1f),
                Shader.TileMode.CLAMP
            )
            canvas.drawRect(0f, 0f, screenW, screenH, paint)
            paint.shader = null
        }
    }

    // -- Helpers shared across draw passes --

    /** Common easeOut grow-in scale for spawn animations. */
    private fun growScale(spawnTime: Float, enterDuration: Float): Float {
        val age = globalTime - spawnTime
        val t = if (enterDuration > 0f) (age / enterDuration).coerceIn(0f, 1f) else 1f
        return 1f - (1f - t).let { it * it * it }
    }

    /** Current wobble offset for a gravity well. Zero when Planet Wobble pref is 0. */
    private fun wobbleDx(well: GravityWell): Float {
        val amp = prefInt(WallpaperSettings.KEY_PLANET_WOBBLE, WallpaperSettings.DEF_PLANET_WOBBLE) / 100f
        if (amp <= 0f) return 0f
        return sin(globalTime * 0.5f + well.wobblePhase) * refSize * 0.04f * amp
    }
    private fun wobbleDy(well: GravityWell): Float {
        val amp = prefInt(WallpaperSettings.KEY_PLANET_WOBBLE, WallpaperSettings.DEF_PLANET_WOBBLE) / 100f
        if (amp <= 0f) return 0f
        return cos(globalTime * 0.37f + well.wobblePhase) * refSize * 0.04f * amp
    }

    // -- Scene initialization --

    private fun initScene() {
        stars.clear()
        shootingStars.clear()
        spiralGalaxies.clear()
        rockets.clear()
        particles.clear()
        debris.clear()
        lastRocketSpawnTime = 0f

        initGravityWells()

        val baseSize = refSize
        val minRadius = baseSize * 0.003f
        val radiusVariation = 1.1f * minRadius
        val minVelocity = baseSize * 0.125f
        val velocityVariation = 2f * minRadius

        // Random stars — extended past the screen so parallax shifts don't
        // reveal empty margins.
        val padX = scenePadX
        val padY = scenePadY
        val spawnW = screenW + 2f * padX
        val spawnH = screenH + 2f * padY
        val starCount = prefInt(WallpaperSettings.KEY_STAR_COUNT, WallpaperSettings.DEF_STAR_COUNT)
        val starSizeMul = prefInt(WallpaperSettings.KEY_STAR_SIZE, WallpaperSettings.DEF_STAR_SIZE) / 100f
        for (i in 0 until starCount) {
            stars.add(Star(
                x = -padX + Random.nextFloat() * spawnW,
                y = -padY + Random.nextFloat() * spawnH,
                radius = (1.5f * minRadius + radiusVariation * Random.nextFloat()) * starSizeMul,
                color = SPACE_COLORS[Random.nextInt(SPACE_COLORS.size)],
                velocityY = minVelocity + velocityVariation * Random.nextFloat(),
                spawnTime = globalTime,
                enterDuration = 0.5f + Random.nextFloat() * 1.0f,
            ))
        }

        // Constellation stars (optional)
        val constellationsEnabled = prefBool(WallpaperSettings.KEY_CONSTELLATIONS_ENABLED, WallpaperSettings.DEF_CONSTELLATIONS_ENABLED)
        val sizeUnit = baseSize / 50f
        val spaceUnit = baseSize / 50f
        if (constellationsEnabled) for (constellation in CONSTELLATIONS) {
            val velocity = minVelocity + velocityVariation * Random.nextFloat()
            val cx = -padX + Random.nextFloat() * spawnW
            val cy = -padY + Random.nextFloat() * spawnH
            val color = SPACE_COLORS[Random.nextInt(SPACE_COLORS.size)]
            for (pt in constellation) {
                stars.add(Star(
                    x = cx + pt.x * spaceUnit,
                    y = cy + pt.y * spaceUnit,
                    radius = sizeUnit * pt.s * 0.5f * starSizeMul,
                    color = color,
                    velocityY = velocity,
                    isConstellation = true,
                    spawnTime = globalTime,
                    enterDuration = 0.5f + Random.nextFloat() * 1.0f,
                ))
            }
        }

        // Shooting stars
        val shootingCount = prefInt(WallpaperSettings.KEY_SHOOTING_STAR_COUNT, WallpaperSettings.DEF_SHOOTING_STAR_COUNT)
        for (i in 0 until shootingCount) {
            shootingStars.add(ShootingStar(screenW, screenH, spawnMarginX, spawnMarginY))
        }

        // Spiral galaxies (toggleable; count 0..3)
        val galaxiesEnabled = prefBool(WallpaperSettings.KEY_GALAXIES_ENABLED, WallpaperSettings.DEF_GALAXIES_ENABLED)
        val galaxyCount = if (!galaxiesEnabled) 0
            else prefInt(WallpaperSettings.KEY_SPIRAL_GALAXY_COUNT, WallpaperSettings.DEF_SPIRAL_GALAXY_COUNT)
        for (g in 0 until galaxyCount) {
            val galaxyRadius = refSize * 0.05f + 0.025f * Random.nextFloat() * refSize
            val rounds = 2 + Random.nextInt(2)
            val pointsPerRound = 70f + 10f * Random.nextFloat()
            val totalSteps = (pointsPerRound * rounds).toInt()
            val totalAngle = rounds * 2f * PI.toFloat()
            val step = totalAngle / totalSteps
            val spiralStars = mutableListOf<SpiralStar>()
            for (i in 0 until totalSteps) {
                val angle = i * step
                val length = galaxyRadius * (i.toFloat() / totalSteps)
                val mx = cos(angle) * length
                val my = sin(angle) * length
                val microLength = 0.15f * (galaxyRadius / rounds)
                val microAngle = 2f * PI.toFloat() * Random.nextFloat()
                val mmx = cos(microAngle) * microLength
                val mmy = sin(microAngle) * microLength
                spiralStars.add(SpiralStar(
                    localX = mx + mmx,
                    localY = my + mmy,
                    radius = galaxyRadius * (0.05f + 0.1f * Random.nextFloat()),
                    color = SPACE_COLORS[Random.nextInt(SPACE_COLORS.size)],
                ))
            }
            spiralGalaxies.add(SpiralGalaxy(
                centerX = -padX + Random.nextFloat() * spawnW,
                centerY = -padY + Random.nextFloat() * spawnH,
                galaxyRadius = galaxyRadius,
                velocityY = 0.5f * minVelocity + 0.5f * velocityVariation * Random.nextFloat(),
                stars = spiralStars,
                spawnTime = globalTime,
                enterDuration = 1.5f,
            ))
        }

        initialized = true
        // Every fresh scene opens with a warp — the "level just started" feel.
        warping = true
        warpElapsed = 0f
    }

    private fun initGravityWells() {
        gravityWells.clear()
        val baseSize = refSize
        val planetCount = prefInt(WallpaperSettings.KEY_PLANET_COUNT, WallpaperSettings.DEF_PLANET_COUNT)
        val gravStrength = prefInt(WallpaperSettings.KEY_GRAVITY_STRENGTH, WallpaperSettings.DEF_GRAVITY_STRENGTH) / 100f
        val gravRadiusPref = prefInt(WallpaperSettings.KEY_GRAVITY_RADIUS, WallpaperSettings.DEF_GRAVITY_RADIUS) / 100f
        val centerClear = prefInt(WallpaperSettings.KEY_CENTER_CLEAR, WallpaperSettings.DEF_CENTER_CLEAR) / 100f

        val midX = screenW / 2f
        val midY = screenH / 2f
        val spread = maxOf(0.05f, centerClear)
        val radiusX = midX * spread
        val radiusY = midY * spread

        // Allow planets to sit slightly past the edge so parallax reveals them
        // naturally. Matches the padding used for stars.
        val padX = scenePadX
        val padY = scenePadY
        // Gravity Strength is the *base*; each planet scales its pull by how
        // much its gravity radius exceeds the pref-configured baseline.
        val baselineRadius = baseSize * gravRadiusPref
        for (i in 0 until planetCount) {
            val angle = (2f * PI.toFloat() * i / planetCount) + Random.nextFloat() * 0.6f - 0.3f
            val dist = 0.4f + 0.6f * Random.nextFloat()
            val cx = (midX + cos(angle) * radiusX * dist).coerceIn(-padX, screenW + padX)
            val cy = (midY + sin(angle) * radiusY * dist).coerceIn(-padY, screenH + padY)

            val gravRadius = baseSize * (gravRadiusPref + 0.25f * Random.nextFloat())
            val radiusScale = if (baselineRadius > 0.01f) gravRadius / baselineRadius else 1f
            val planetSizePref = prefInt(WallpaperSettings.KEY_PLANET_SIZE, WallpaperSettings.DEF_PLANET_SIZE) / 100f
            val planetRadius = baseSize * planetSizePref * (0.06f + 0.03f * Random.nextFloat())
            gravityWells.add(GravityWell(
                x = cx, y = cy,
                gravityRadius = gravRadius,
                attractionForce = baseSize * gravStrength * radiusScale,
                colorIndex = (i + 1) % PLANET_COLORS.size,
                planetRadius = planetRadius,
                rotationSpeed = 0.5f * PI.toFloat() * Random.nextFloat() * (if (Random.nextBoolean()) 1f else -1f),
                spawnTime = globalTime,
                enterDuration = 1.0f + Random.nextFloat() * 1.0f,
            ))
        }
    }

    // -- Rockets --

    private fun spawnRocket() {
        val cap = prefInt(WallpaperSettings.KEY_MAX_LIVE_ROCKETS, WallpaperSettings.DEF_MAX_LIVE_ROCKETS)
        if (cap > 0 && rockets.count { it.alive } >= cap) return
        val sizeMul = prefInt(WallpaperSettings.KEY_ROCKET_SIZE, WallpaperSettings.DEF_ROCKET_SIZE) / 100f
        val rocketRadius = refSize * 0.025f * sizeMul
        val rocketWidth = rocketRadius * 2f
        val speedFactor = prefInt(WallpaperSettings.KEY_ROCKET_SPEED, WallpaperSettings.DEF_ROCKET_SPEED) / 10f
        val minVel = (0.4f + 0.4f * Random.nextFloat()) * rocketWidth * 2f
        var vx = minVel + Random.nextFloat() * rocketWidth * speedFactor
        var vy = minVel + Random.nextFloat() * rocketWidth * speedFactor

        // Spawn beyond the parallax reveal region, not at the bare screen edge.
        val padX = spawnMarginX
        val padY = spawnMarginY
        var rx: Float; var ry: Float
        when (Random.nextInt(4)) {
            0 -> { rx = -rocketWidth - padX; ry = -padY + Random.nextFloat() * (screenH + 2f * padY) }
            1 -> { rx = screenW + rocketWidth + padX; ry = -padY + Random.nextFloat() * (screenH + 2f * padY) }
            2 -> { rx = -padX + Random.nextFloat() * (screenW + 2f * padX); ry = -rocketWidth - padY }
            else -> { rx = -padX + Random.nextFloat() * (screenW + 2f * padX); ry = screenH + rocketWidth + padY }
        }

        // Aim the rocket through the viewport: keep the tuned speed magnitude,
        // direct it at a random point in the central 76% of the screen.
        val speed = kotlin.math.hypot(vx, vy)
        val tx = screenW * (0.12f + 0.76f * Random.nextFloat())
        val ty = screenH * (0.12f + 0.76f * Random.nextFloat())
        val dist = kotlin.math.hypot(tx - rx, ty - ry).coerceAtLeast(1f)
        vx = (tx - rx) / dist * speed
        vy = (ty - ry) / dist * speed

        rockets.add(EngineRocket(
            x = rx, y = ry,
            vx = vx, vy = vy,
            radius = rocketRadius,
            type = RocketType.random(),
        ))
    }

    private fun drawRocket(canvas: Canvas, rocket: EngineRocket) {
        val rocketW = rocket.radius * 2f
        val rocketH = rocket.radius * 3f
        val angle = atan2(rocket.vx, -rocket.vy)
        val angleDeg = toDegrees(angle)
        val useSprites = prefBool(WallpaperSettings.KEY_USE_SPRITES, WallpaperSettings.DEF_USE_SPRITES)

        canvas.save()
        canvas.translate(rocket.x, rocket.y)
        canvas.rotate(angleDeg)

        // Neon halo — three concentric soft circles, drawn before the rocket so
        // the body + highlights stay crisp on top.
        val glowMul = prefInt(WallpaperSettings.KEY_ROCKET_GLOW, WallpaperSettings.DEF_ROCKET_GLOW) / 100f
        if (glowMul > 0f) {
            paint.style = Paint.Style.FILL
            paint.shader = null
            paint.colorFilter = null
            val glowColor = rocket.type.color
            paint.color = withOpacity(glowColor, 0.10f * glowMul)
            canvas.drawCircle(0f, 0f, rocket.radius * 2.6f, paint)
            paint.color = withOpacity(glowColor, 0.18f * glowMul)
            canvas.drawCircle(0f, 0f, rocket.radius * 1.8f, paint)
            paint.color = withOpacity(brightenColor(glowColor, 0.4f), 0.32f * glowMul)
            canvas.drawCircle(0f, 0f, rocket.radius * 1.25f, paint)
        }

        if (useSprites) {
            val bitmap = rocketBitmaps[rocket.type]
            if (bitmap != null) {
                val scaleX = rocketW / bitmap.width
                val scaleY = rocketH / bitmap.height

                rocketMatrix.reset(); rocketMatrix.setScale(scaleX * 1.1f, scaleY * 1.1f)
                rocketMatrix.postTranslate(-rocketW * 1.1f / 2f, -rocketH * 1.1f / 2f)
                paint.colorFilter = rocketOuterFilter[rocket.type]
                paint.alpha = 255
                canvas.drawBitmap(bitmap, rocketMatrix, paint)

                rocketMatrix.reset(); rocketMatrix.setScale(scaleX * 1.05f, scaleY * 1.05f)
                rocketMatrix.postTranslate(-rocketW * 1.05f / 2f, -rocketH * 1.05f / 2f)
                paint.colorFilter = rocketMidFilter[rocket.type]
                canvas.drawBitmap(bitmap, rocketMatrix, paint)

                rocketMatrix.reset(); rocketMatrix.setScale(scaleX, scaleY)
                rocketMatrix.postTranslate(-rocketW / 2f, -rocketH / 2f)
                paint.colorFilter = null
                canvas.drawBitmap(bitmap, rocketMatrix, paint)

                paint.colorFilter = rocketGlintFilter[rocket.type]
                canvas.drawBitmap(bitmap, rocketMatrix, paint)
                paint.colorFilter = null
            }
        } else {
            paint.style = Paint.Style.FILL
            paint.color = withOpacity(brightenColor(rocket.type.color, 0.9f), 0.25f)
            canvas.drawCircle(0f, 0f, rocketW * 0.8f, paint)

            bodyPath.reset()
            bodyPath.moveTo(0f, -rocketH / 2f)
            bodyPath.lineTo(-rocketW / 2f * 0.6f, rocketH / 2f)
            bodyPath.lineTo(rocketW / 2f * 0.6f, rocketH / 2f)
            bodyPath.close()
            paint.color = withOpacity(brightenColor(rocket.type.color, 0.3f), 0.9f)
            canvas.drawPath(bodyPath, paint)

            paint.color = withOpacity(Color.WHITE, 0.5f)
            canvas.drawCircle(0f, -rocketH * 0.15f, rocketW * 0.12f, paint)
        }

        canvas.restore()
    }

    // -- Particles --

    private fun spawnPropulsionParticle(rocket: EngineRocket) {
        val propRadius = rocket.radius * 0.3f
        val angle = atan2(rocket.vx, -rocket.vy)
        val backX = rocket.x - sin(angle) * rocket.radius * 1.2f
        val backY = rocket.y + cos(angle) * rocket.radius * 1.2f

        particles.add(PropulsionParticle(
            x = backX + (Random.nextFloat() - 0.5f) * propRadius,
            y = backY + (Random.nextFloat() - 0.5f) * propRadius,
            vx = -rocket.vx * 0.1f,
            vy = -rocket.vy * 0.1f,
            radius = propRadius * (1f + 0.5f * Random.nextFloat()),
            color = rocket.type.color,
        ))
    }

    private fun drawPropulsionParticle(canvas: Canvas, p: PropulsionParticle, lifetime: Float) {
        val factor = 1f - (p.elapsed / lifetime).coerceIn(0f, 1f)
        paint.style = Paint.Style.FILL
        paint.color = withOpacity(p.color, 0.8f * factor)
        canvas.drawCircle(p.x, p.y, p.radius * factor, paint)
        paint.color = withOpacity(Color.WHITE, 0.6f * factor)
        canvas.drawCircle(p.x, p.y, p.radius * 0.4f * factor, paint)
    }

    // -- Collisions --

    private fun checkRocketCollisions(lowPower: Boolean) {
        for (i in rockets.indices) {
            val r1 = rockets[i]
            if (!r1.alive) continue
            for (j in i + 1 until rockets.size) {
                val r2 = rockets[j]
                if (!r2.alive) continue
                val dx = r1.x - r2.x; val dy = r1.y - r2.y
                if (sqrt(dx * dx + dy * dy) < r1.radius + r2.radius) {
                    spawnDebrisFromCollision(r1, r2, lowPower)
                    r1.alive = false; r2.alive = false
                }
            }
        }
    }

    // -- Debris --

    private fun spawnDebrisFromCollision(r1: EngineRocket, r2: EngineRocket, lowPower: Boolean) {
        val rawPieces = prefInt(WallpaperSettings.KEY_DEBRIS_COUNT, WallpaperSettings.DEF_DEBRIS_COUNT)
        val pieces = if (lowPower) (rawPieces / 2).coerceAtLeast(1) else rawPieces
        val newRadius = r1.radius / (pieces / 4f)
        val midX = (r1.x + r2.x) / 2f
        val midY = (r1.y + r2.y) / 2f
        val speed = max(
            sqrt(r1.vx * r1.vx + r1.vy * r1.vy),
            sqrt(r2.vx * r2.vx + r2.vy * r2.vy)
        ) * 0.8f

        for (i in 0 until pieces) {
            val angle = Random.nextFloat() * 2f * PI.toFloat()
            val vel = speed * (0.4f + 0.6f * Random.nextFloat())
            val type = if (Random.nextBoolean()) r1.type else r2.type

            debris.add(Debris(
                x = midX + cos(angle) * newRadius,
                y = midY + sin(angle) * newRadius,
                vx = cos(angle) * vel,
                vy = sin(angle) * vel,
                radius = newRadius * (0.5f + Random.nextFloat()),
                color = type.color,
                rocketType = type,
                spriteIndex = Random.nextInt(type.debrisAssets.size),
            ))
        }
    }

    private fun spawnDebrisFromPlanetHit(rocket: EngineRocket, @Suppress("UNUSED_PARAMETER") well: GravityWell, lowPower: Boolean) {
        val rawPieces = prefInt(WallpaperSettings.KEY_DEBRIS_COUNT, WallpaperSettings.DEF_DEBRIS_COUNT)
        val pieces = if (lowPower) (rawPieces / 2).coerceAtLeast(1) else rawPieces
        val newRadius = rocket.radius / (pieces / 4f)
        val speed = sqrt(rocket.vx * rocket.vx + rocket.vy * rocket.vy) * 0.8f

        for (i in 0 until pieces) {
            val angle = Random.nextFloat() * 2f * PI.toFloat()
            val vel = speed * (0.4f + 0.6f * Random.nextFloat())

            debris.add(Debris(
                x = rocket.x + cos(angle) * newRadius,
                y = rocket.y + sin(angle) * newRadius,
                vx = cos(angle) * vel,
                vy = sin(angle) * vel,
                radius = newRadius * (0.5f + Random.nextFloat()),
                color = rocket.type.color,
                rocketType = rocket.type,
                spriteIndex = Random.nextInt(rocket.type.debrisAssets.size),
            ))
        }
    }

    private fun drawDebris(canvas: Canvas, d: Debris, debrisLifetime: Float) {
        val fadeStart = debrisLifetime * 0.6f
        val fadeDuration = debrisLifetime * 0.4f
        val alpha = if (d.elapsed > fadeStart) {
            (1f - (d.elapsed - fadeStart) / fadeDuration).coerceIn(0f, 1f)
        } else 1f
        if (alpha <= 0f) return

        val useSprites = prefBool(WallpaperSettings.KEY_USE_SPRITES, WallpaperSettings.DEF_USE_SPRITES)

        canvas.save()
        canvas.translate(d.x, d.y)

        // Neon halo — drawn before rotation so the rotating fragment tumbles
        // inside a fixed halo.
        val glowMul = prefInt(WallpaperSettings.KEY_DEBRIS_GLOW, WallpaperSettings.DEF_DEBRIS_GLOW) / 100f
        if (glowMul > 0f) {
            paint.style = Paint.Style.FILL
            paint.shader = null
            paint.colorFilter = null
            paint.alpha = 255
            val ga = glowMul * alpha
            paint.color = withOpacity(d.color, 0.10f * ga)
            canvas.drawCircle(0f, 0f, d.radius * 2.4f, paint)
            paint.color = withOpacity(d.color, 0.18f * ga)
            canvas.drawCircle(0f, 0f, d.radius * 1.7f, paint)
            paint.color = withOpacity(brightenColor(d.color, 0.4f), 0.30f * ga)
            canvas.drawCircle(0f, 0f, d.radius * 1.2f, paint)
        }

        canvas.rotate(toDegrees(d.angle))

        if (useSprites) {
            val bitmaps = debrisBitmaps[d.rocketType]
            if (bitmaps == null) { canvas.restore(); return }
            val bitmap = bitmaps[d.spriteIndex % bitmaps.size]
            val drawSize = d.radius * 2f * 1.4f
            val scaleX = drawSize / bitmap.width
            val scaleY = drawSize / bitmap.height

            debrisMatrix.reset(); debrisMatrix.setScale(scaleX * 1.4f, scaleY * 1.4f)
            debrisMatrix.postTranslate(-drawSize * 1.4f / 2f, -drawSize * 1.4f / 2f)
            paint.colorFilter = debrisTintFilter[d.rocketType]
            paint.alpha = (128 * alpha).toInt().coerceIn(0, 255)
            canvas.drawBitmap(bitmap, debrisMatrix, paint)

            debrisMatrix.reset(); debrisMatrix.setScale(scaleX, scaleY)
            debrisMatrix.postTranslate(-drawSize / 2f, -drawSize / 2f)
            paint.colorFilter = null
            paint.alpha = (255 * alpha).toInt().coerceIn(0, 255)
            canvas.drawBitmap(bitmap, debrisMatrix, paint)
            paint.alpha = 255
        } else {
            paint.style = Paint.Style.FILL
            paint.color = withOpacity(brightenColor(d.color, 0.8f), 0.5f * alpha)
            canvas.drawCircle(0f, 0f, d.radius * 1.4f, paint)
            bodyPath.reset()
            bodyPath.moveTo(0f, -d.radius)
            bodyPath.lineTo(d.radius * 0.7f, d.radius * 0.5f)
            bodyPath.lineTo(-d.radius * 0.7f, d.radius * 0.5f)
            bodyPath.close()
            paint.color = withOpacity(d.color, 0.8f * alpha)
            canvas.drawPath(bodyPath, paint)
        }

        canvas.restore()
    }

    // -- Stars (sparkle + layered glow) --

    private fun drawStar(canvas: Canvas, star: Star, warpFactor: Float) {
        val age = globalTime - star.spawnTime
        val enterP = if (star.enterDuration > 0f) (age / star.enterDuration).coerceIn(0f, 1f) else 1f
        val grow = 1f - (1f - enterP).let { it * it * it }
        if (grow < 0.001f) return
        val glowMul = prefInt(WallpaperSettings.KEY_STAR_GLOW, WallpaperSettings.DEF_STAR_GLOW) / 100f

        val t = globalTime - star.waitOffset
        val factor = if (t < 0f) {
            (1f + t / star.waitOffset).coerceIn(0f, 1f)
        } else {
            (1f - star.shiningFactor) + star.shiningFactor * abs(sin(t / star.shiningDuration))
        }

        val drawRadius = star.radius * factor * grow
        val isWarping = warpFactor > 0.01f

        // Warp streak — drawn radially from the star toward the screen center.
        if (isWarping && !star.isConstellation) {
            val cx = screenW / 2f
            val cy = screenH / 2f
            val dx = star.x - cx
            val dy = star.y - cy
            val dist = sqrt(dx * dx + dy * dy).coerceAtLeast(1f)
            val streakLength = (2f * star.radius + 0.18f * dist) * warpFactor
            val tx = star.x - (dx / dist) * streakLength
            val ty = star.y - (dy / dist) * streakLength
            paint.color = withOpacity(Color.WHITE, 0.8f * warpFactor)
            paint.strokeWidth = 0.2f * star.radius + 0.5f
            paint.style = Paint.Style.STROKE
            canvas.drawLine(tx, ty, star.x, star.y, paint)
        }

        val bmp = starBitmap
        val bmpHalf = starBitmapSize / 2f

        // Bright core — always a white bitmap at 0.81 alpha; clamp the drawn
        // radius so stars never dip below ~1 px during the shining animation.
        val coreScale = 0.3f
        val coreR = (drawRadius * coreScale).coerceAtLeast(1f)
        paint.colorFilter = null
        paint.style = Paint.Style.FILL
        paint.alpha = (0.81f * 255f).toInt()
        canvas.save()
        canvas.translate(star.x, star.y)
        canvas.scale(coreR / bmpHalf, coreR / bmpHalf)
        canvas.drawBitmap(bmp, -bmpHalf, -bmpHalf, paint)
        canvas.restore()

        // Glow layers — tinted via cached color filter (warp filter during warp,
        // steady filter otherwise). Same min-radius clamp so glow doesn't vanish.
        val tint = if (isWarping) warpGlowFilter[star.color] else starGlowFilter[star.color]
        if (tint != null && glowMul > 0f) {
            paint.colorFilter = tint
            val layers = 3
            for (i in 0 until layers) {
                val s = 1f - (i.toFloat() / layers) * 0.6f
                val layerR = (drawRadius * s).coerceAtLeast(1f)
                paint.alpha = ((0.02f + 0.03f * s) * glowMul * 255f).toInt().coerceIn(0, 255)
                canvas.save()
                canvas.translate(star.x, star.y)
                canvas.scale(layerR / bmpHalf, layerR / bmpHalf)
                canvas.drawBitmap(bmp, -bmpHalf, -bmpHalf, paint)
                canvas.restore()
            }
            paint.colorFilter = null
        }
        paint.alpha = 255
    }

    private fun drawSpiralGalaxy(canvas: Canvas, galaxy: SpiralGalaxy) {
        val age = globalTime - galaxy.spawnTime
        val enterP = (age / galaxy.enterDuration).coerceIn(0f, 1f)
        val grow = 1f - (1f - enterP).let { it * it * it }
        if (grow < 0.001f) return

        canvas.save()
        canvas.translate(galaxy.centerX, galaxy.centerY)
        canvas.scale(grow, grow)
        canvas.rotate(toDegrees(galaxy.angle))
        canvas.scale(galaxy.aspectRatio, 1f)

        for (ss in galaxy.stars) {
            val factor = (1f - ss.shiningFactor) + ss.shiningFactor * abs(sin(globalTime / ss.shiningDuration))
            val r = ss.radius * factor

            paint.style = Paint.Style.FILL
            paint.color = withOpacity(ss.color, 0.04f)
            canvas.drawCircle(ss.localX, ss.localY, r * 2f, paint)
            paint.color = withOpacity(brightenColor(ss.color, 1f), 0.6f)
            canvas.drawCircle(ss.localX, ss.localY, r * 0.3f, paint)
        }

        canvas.restore()
    }

    private fun updateAndDrawShootingStar(canvas: Canvas, ss: ShootingStar, dt: Float) {
        ss.elapsed += dt
        when (ss.state) {
            ShootingStarState.WAITING -> {
                if (ss.elapsed > ss.waitingDuration) {
                    ss.state = ShootingStarState.SHOOTING
                    ss.elapsed = 0f
                }
            }
            ShootingStarState.SHOOTING -> {
                val timeDiff = 0.5f
                if (ss.elapsed - timeDiff > ss.fallingDuration) {
                    ss.state = ShootingStarState.WAITING
                    ss.elapsed = 0f
                    ss.resetPositions()
                    return
                }
                val pathX = (ss.toX - ss.fromX) * 1.5f
                val pathY = (ss.toY - ss.fromY) * 1.5f
                val p = easeOutExpo((ss.elapsed / ss.fallingDuration).coerceIn(0f, 1f))
                val pt = easeOutExpo(((ss.elapsed - timeDiff) / ss.fallingDuration).coerceIn(0f, 1f))

                val startX = ss.fromX + pathX * pt
                val startY = ss.fromY + pathY * pt
                val endXBase = ss.fromX + pathX * p
                val endYBase = ss.fromY + pathY * p

                // Stretch/compress trail from its lead point
                val lenMul = prefInt(WallpaperSettings.KEY_SHOOTING_STAR_LENGTH, WallpaperSettings.DEF_SHOOTING_STAR_LENGTH) / 100f
                val brightMul = prefInt(WallpaperSettings.KEY_SHOOTING_STAR_BRIGHTNESS, WallpaperSettings.DEF_SHOOTING_STAR_BRIGHTNESS) / 100f
                val drawStartX = endXBase - (endXBase - startX) * lenMul
                val drawStartY = endYBase - (endYBase - startY) * lenMul

                paint.color = withOpacity(Color.WHITE, 0.4685f * (1f - p) * brightMul)
                paint.strokeWidth = 0.5f + 1f * (1f - p)
                paint.style = Paint.Style.STROKE
                canvas.drawLine(drawStartX, drawStartY, endXBase, endYBase, paint)
            }
        }
    }

    // -- Planets --

    private fun drawPlanet(canvas: Canvas, well: GravityWell) {
        val scale = growScale(well.spawnTime, well.enterDuration)
        if (scale < 0.001f) return

        val pr = well.planetRadius * scale
        val rotMul = prefInt(WallpaperSettings.KEY_PLANET_ROTATION, WallpaperSettings.DEF_PLANET_ROTATION) / 100f
        val angleDeg = toDegrees(well.rotationSpeed * globalTime * rotMul)
        val useSprites = prefBool(WallpaperSettings.KEY_USE_SPRITES, WallpaperSettings.DEF_USE_SPRITES)
        val planetColor = PLANET_COLORS[well.colorIndex % PLANET_COLORS.size]

        canvas.save()
        canvas.translate(well.x + wobbleDx(well), well.y + wobbleDy(well))

        // Neon halo around the planet — drawn before rotation so the halo stays
        // stationary while the planet bitmap spins.
        val glowMul = prefInt(WallpaperSettings.KEY_PLANET_GLOW, WallpaperSettings.DEF_PLANET_GLOW) / 100f
        if (glowMul > 0f) {
            paint.style = Paint.Style.FILL
            paint.shader = null
            paint.colorFilter = null
            paint.alpha = 255
            val ga = glowMul * scale
            paint.color = withOpacity(planetColor, 0.08f * ga)
            canvas.drawCircle(0f, 0f, pr * 1.8f, paint)
            paint.color = withOpacity(planetColor, 0.14f * ga)
            canvas.drawCircle(0f, 0f, pr * 1.4f, paint)
            paint.color = withOpacity(brightenColor(planetColor, 0.3f), 0.22f * ga)
            canvas.drawCircle(0f, 0f, pr * 1.15f, paint)
        }

        canvas.rotate(angleDeg)

        if (useSprites && planetBitmaps.isNotEmpty()) {
            val bitmap = planetBitmaps[well.colorIndex % planetBitmaps.size]
            val drawSize = pr * 2f
            val scaleX = drawSize / bitmap.width
            val scaleY = drawSize / bitmap.height
            planetMatrix.reset()
            planetMatrix.setScale(scaleX, scaleY)
            planetMatrix.postTranslate(-pr, -pr)
            paint.alpha = (255 * scale).toInt().coerceIn(0, 255)
            canvas.drawBitmap(bitmap, planetMatrix, paint)

            if (cloudBitmaps.isNotEmpty()) {
                val cloud = cloudBitmaps[well.colorIndex % cloudBitmaps.size]
                val cScaleX = drawSize / cloud.width
                val cScaleY = drawSize / cloud.height
                planetMatrix.reset()
                planetMatrix.setScale(cScaleX, cScaleY)
                planetMatrix.postTranslate(-pr, -pr)
                canvas.drawBitmap(cloud, planetMatrix, paint)
            }
            paint.alpha = 255
        } else {
            val color = PLANET_COLORS[well.colorIndex % PLANET_COLORS.size]
            paint.style = Paint.Style.FILL
            paint.color = withOpacity(brightenColor(color, 0.7f), 0.15f * scale)
            canvas.drawCircle(0f, 0f, pr * 1.6f, paint)
            paint.color = withOpacity(brightenColor(color, 0.5f), 0.3f * scale)
            canvas.drawCircle(0f, 0f, pr * 1.2f, paint)
            paint.color = withOpacity(color, 0.9f * scale)
            canvas.drawCircle(0f, 0f, pr, paint)
            paint.color = withOpacity(Color.WHITE, 0.4f * scale)
            canvas.drawCircle(-pr * 0.25f, -pr * 0.25f, pr * 0.35f, paint)
        }

        canvas.restore()
    }

    // -- Gravity --

    private inline fun applyGravityToBody(
        bx: Float, by: Float, bodyRadius: Float, forceMultiplier: Float,
        applyAccel: (Float, Float) -> Unit
    ) {
        if (!prefBool(WallpaperSettings.KEY_GRAVITY_ENABLED, WallpaperSettings.DEF_GRAVITY_ENABLED)) return
        for (well in gravityWells) {
            val scale = growScale(well.spawnTime, well.enterDuration)
            val scaledRadius = well.gravityRadius * scale
            val wx = well.x + wobbleDx(well)
            val wy = well.y + wobbleDy(well)
            val dx = wx - bx
            val dy = wy - by
            val dist = sqrt(dx * dx + dy * dy)
            if (dist <= scaledRadius + bodyRadius && dist > 1f) {
                val dirX = dx / dist; val dirY = dy / dist
                val effectiveDist = dist.coerceAtLeast(scaledRadius * 0.15f)
                val falloff = 1f - (effectiveDist / scaledRadius).coerceIn(0f, 1f)
                val force = well.attractionForce * scale * falloff * forceMultiplier
                applyAccel(dirX * force, dirY * force)
            }
        }
    }

    // -- Pref helpers --

    private fun prefInt(key: String, default: Int) = WallpaperSettings.getInt(key, default)
    private fun prefBool(key: String, default: Boolean) = WallpaperSettings.getBoolean(key, default)

    // -- Nested types --

    enum class SizeChange { INITIAL, RESIZED, UNCHANGED }

    private enum class ShootingStarState { WAITING, SHOOTING }

    private data class Star(
        var x: Float,
        var y: Float,
        val radius: Float,
        val color: Int,
        val velocityY: Float,
        val shiningDuration: Float = Random.nextFloat() * 1f + 0.75f,
        val shiningFactor: Float = 0.5f + Random.nextFloat() * 0.25f,
        val waitOffset: Float = Random.nextFloat() * 2f,
        val isConstellation: Boolean = false,
        val spawnTime: Float = 0f,
        val enterDuration: Float = 1.0f,
    )

    private data class ShootingStar(
        val screenW: Float,
        val screenH: Float,
        /** Reveal region added by parallax; trails start and end past it. */
        val padX: Float = 0f,
        val padY: Float = 0f,
        var fromX: Float = 0f,
        var fromY: Float = 0f,
        var toX: Float = 0f,
        var toY: Float = 0f,
        var state: ShootingStarState = ShootingStarState.WAITING,
        var elapsed: Float = 0f,
        var waitingDuration: Float = Random.nextFloat() * 3f + 2f,
        var fallingDuration: Float = Random.nextFloat() * 1.2f + 1.25f,
    ) {
        init { resetPositions() }

        fun resetPositions() {
            val edges = listOf(
                { Pair(-padX + Random.nextFloat() * (screenW + 2f * padX), -padY) },
                { Pair(screenW + padX, -padY + Random.nextFloat() * (screenH + 2f * padY)) },
                { Pair(-padX + Random.nextFloat() * (screenW + 2f * padX), screenH + padY) },
                { Pair(-padX, -padY + Random.nextFloat() * (screenH + 2f * padY)) },
            )
            val i1 = Random.nextInt(4)
            var i2: Int
            do { i2 = Random.nextInt(4) } while (i2 == i1)
            val (fx, fy) = edges[i1]()
            val (tx, ty) = edges[i2]()
            fromX = fx; fromY = fy; toX = tx; toY = ty
            elapsed = 0f
            waitingDuration = Random.nextFloat() * 3f + 2f
            fallingDuration = Random.nextFloat() * 1.2f + 1.25f
        }
    }

    private data class SpiralStar(
        var localX: Float,
        var localY: Float,
        val radius: Float,
        val color: Int,
        val shiningDuration: Float = 0.75f + 0.5f * Random.nextFloat(),
        val shiningFactor: Float = 0.25f,
    )

    private data class SpiralGalaxy(
        var centerX: Float,
        var centerY: Float,
        val galaxyRadius: Float,
        val velocityY: Float,
        val aspectRatio: Float = 1f,
        var angle: Float = 0f,
        val stars: List<SpiralStar>,
        var spawnTime: Float = 0f,  // same reason as GravityWell.spawnTime
        val enterDuration: Float = 1.5f,
    )

    private data class GravityWell(
        val x: Float,
        val y: Float,
        val gravityRadius: Float,
        val attractionForce: Float,
        val colorIndex: Int = 0,
        val planetRadius: Float = 0f,
        val rotationSpeed: Float = 0f,
        val wobblePhase: Float = Random.nextFloat() * 2f * PI.toFloat(),
        // spawnTime is mutable: refreshed when the intro warp ends so planets
        // grow in right after the warp, not invisibly during it.
        var spawnTime: Float = 0f,
        val enterDuration: Float = 1.5f,
    )

    private data class EngineRocket(
        var x: Float,
        var y: Float,
        var vx: Float,
        var vy: Float,
        val radius: Float,
        val type: RocketType,
        var elapsed: Float = 0f,
        var alive: Boolean = true,
        val behindWells: MutableSet<Int> = mutableSetOf(),
        val wasOverlappingCore: MutableSet<Int> = mutableSetOf(),
    )

    private data class PropulsionParticle(
        var x: Float,
        var y: Float,
        var vx: Float,
        var vy: Float,
        var radius: Float,
        val color: Int,
        var elapsed: Float = 0f,
    )

    private data class Debris(
        var x: Float,
        var y: Float,
        var vx: Float,
        var vy: Float,
        val radius: Float,
        val color: Int,
        val rocketType: RocketType,
        val spriteIndex: Int,
        var angle: Float = Random.nextFloat() * 2f * PI.toFloat(),
        val rotationSpeed: Float = PI.toFloat() + PI.toFloat() * Random.nextFloat() * (if (Random.nextBoolean()) 1f else -1f),
        var elapsed: Float = 0f,
    )

    enum class RocketType(val color: Int, val asset: String, val debrisAssets: List<String>) {
        GREEN(Color.GREEN, "rocket1", listOf(
            "nave_a_01", "nave_a_02", "nave_a_03",
            "nave_a_04", "nave_a_05", "nave_a_06",
        )),
        BLUE(Color.rgb(96, 125, 139), "rocket2", listOf(
            "nave_b_01", "nave_b_02", "nave_b_03",
            "nave_b_04", "nave_b_05",
        )),
        YELLOW(Color.rgb(255, 152, 0), "rocket3", listOf(
            "nave_c_01", "nave_c_02", "nave_c_03",
            "nave_c_04", "nave_c_05",
        )),
        PURPLE(Color.rgb(156, 39, 176), "rocket4", listOf(
            "nave_d_01", "nave_d_02", "nave_d_03",
            "nave_d_04", "nave_d_05", "nave_d_06",
        ));

        companion object {
            fun random(): RocketType = entries[Random.nextInt(entries.size)]
        }
    }

    private data class ConstellationPoint(val x: Float, val y: Float, val s: Float)

    companion object {
        private val BG_COLOR = Color.rgb(2, 0, 9)

        private fun toDegrees(rad: Float): Float = rad * (180f / PI.toFloat())

        private val SPACE_COLORS = intArrayOf(
            darkenColor(Color.rgb(255, 0, 0), 0.55f),
            darkenColor(Color.rgb(0, 0, 255), 0.55f),
            darkenColor(Color.rgb(255, 0, 255), 0.55f),
        )

        private val PLANET_COLORS = intArrayOf(
            Color.rgb(180, 120, 80),
            Color.rgb(80, 130, 180),
            Color.rgb(200, 180, 130),
            Color.rgb(100, 170, 100),
            Color.rgb(170, 100, 160),
            Color.rgb(140, 160, 180),
            Color.rgb(200, 100, 80),
            Color.rgb(120, 140, 100),
        )

        private val CONSTELLATIONS: List<List<ConstellationPoint>> = listOf(
            listOf(ConstellationPoint(-2.5f, 2f, 2f), ConstellationPoint(1.45f, -0.5f, 2f), ConstellationPoint(1.8f, -2.77f, 1f)),
            listOf(ConstellationPoint(-3.2f, 3.1f, 2f), ConstellationPoint(2.5f, -0.87f, 1f), ConstellationPoint(3f, -1.5f, 1f), ConstellationPoint(3.9f, -2.5f, 1f), ConstellationPoint(2.4f, -2.3f, 1f), ConstellationPoint(1.5f, -1.9f, 2f), ConstellationPoint(-4.4f, 0f, 1f)),
            listOf(ConstellationPoint(4.4f, -4.1f, 2f), ConstellationPoint(0.9f, -2.2f, 1f), ConstellationPoint(-1.1f, -1.5f, 1f), ConstellationPoint(-4f, 1.5f, 2f), ConstellationPoint(-2.5f, 3.1f, 2f), ConstellationPoint(3f, 0f, 1f), ConstellationPoint(5.2f, -1.1f, 1f), ConstellationPoint(6.2f, -1.1f, 1f)),
            listOf(ConstellationPoint(-1.8f, -3.3f, 1f), ConstellationPoint(0f, -0.5f, 1f), ConstellationPoint(2.9f, -4f, 1f), ConstellationPoint(0f, 0.98f, 1f), ConstellationPoint(-0.25f, 4.15f, 1f)),
            listOf(ConstellationPoint(-3.1f, -0.67f, 2f), ConstellationPoint(-4.2f, -1.8f, 1f), ConstellationPoint(-4.67f, -2.475f, 1f), ConstellationPoint(-3.6f, -3.499f, 2f), ConstellationPoint(-1.99f, -3.5f, 1f), ConstellationPoint(-0.3f, -3f, 1f), ConstellationPoint(-0.2f, -1.25f, 1f), ConstellationPoint(0f, 0f, 2f), ConstellationPoint(1.3f, 2.35f, 1f), ConstellationPoint(2f, 3.1f, 2f), ConstellationPoint(3f, 3.75f, 1f), ConstellationPoint(4.6f, 5.8f, 1f), ConstellationPoint(4.9f, 4.5f, 1f), ConstellationPoint(4.879f, 3.1f, 1f)),
            listOf(ConstellationPoint(-2f, 0.5f, 1f), ConstellationPoint(0.15f, 2.7f, 2f), ConstellationPoint(3f, 0.1f, 2f), ConstellationPoint(1.3f, -4.6f, 1f)),
            listOf(ConstellationPoint(-4f, -3.5f, 2f), ConstellationPoint(-2.5f, -1.3f, 1f), ConstellationPoint(-4.9f, 0.25f, 1f), ConstellationPoint(0f, 0f, 2f), ConstellationPoint(-1.2f, 1.8f, 1f), ConstellationPoint(-1.8f, 4.5f, 2f), ConstellationPoint(1.9f, 0.2f, 1f), ConstellationPoint(4.2f, 1.1f, 1f), ConstellationPoint(4.99f, 2.89f, 1f)),
            listOf(ConstellationPoint(2.1f, 1.5f, 2f), ConstellationPoint(3.2f, 0.3f, 1f), ConstellationPoint(3.4f, -1.1f, 2f), ConstellationPoint(-2.5f, 0.1f, 1f), ConstellationPoint(-5f, 0f, 2f), ConstellationPoint(-2.15f, -1.8f, 1f), ConstellationPoint(2.55f, 2.8f, 1f), ConstellationPoint(4.2f, 3.8f, 1f), ConstellationPoint(4.8f, 2.9f, 1f)),
            listOf(ConstellationPoint(-1.3f, 2.6f, 1f), ConstellationPoint(-2.2f, 2.1f, 1f), ConstellationPoint(-1.25f, 0.2f, 2f), ConstellationPoint(-2.3f, -0.45f, 1f), ConstellationPoint(-1.95f, -1.45f, 1f), ConstellationPoint(0f, 0f, 1f), ConstellationPoint(1.46f, 0.3f, 1f), ConstellationPoint(3.1f, 2.2f, 1f), ConstellationPoint(2.2f, -1.4f, 1f), ConstellationPoint(3.77f, -1.87f, 1f), ConstellationPoint(1.7f, -3.1f, 2f), ConstellationPoint(2.2f, -4.3f, 1f)),
            listOf(ConstellationPoint(5.3f, 2.3f, 1f), ConstellationPoint(4.5f, 2.3f, 1f), ConstellationPoint(1.8f, -2.2f, 1f), ConstellationPoint(0.98f, -3f, 1f), ConstellationPoint(-3f, -0.8f, 1f), ConstellationPoint(-5.5f, 1.8f, 2f), ConstellationPoint(-4.2f, 1.75f, 1f), ConstellationPoint(-2.25f, 1.6f, 1f), ConstellationPoint(-0.6f, 1.5f, 1f)),
            listOf(ConstellationPoint(-3.8f, -5.2f, 1f), ConstellationPoint(-2.8f, -3.8f, 1f), ConstellationPoint(-2.5f, -3.1f, 1f), ConstellationPoint(-2.7f, -1.4f, 1f), ConstellationPoint(-1.3f, 0.8f, 1f), ConstellationPoint(-0.5f, 0.85f, 1f), ConstellationPoint(0f, 0f, 1f), ConstellationPoint(1.1f, 0.6f, 2f), ConstellationPoint(3.4f, -0.6f, 2f), ConstellationPoint(6.7f, -2.2f, 1f)),
            listOf(ConstellationPoint(-1.1f, 6.2f, 1f), ConstellationPoint(-2.4f, 5.5f, 1f), ConstellationPoint(-2f, 4.8f, 1f), ConstellationPoint(-1.9f, 3.7f, 1f), ConstellationPoint(-3f, 2.4f, 2f), ConstellationPoint(-4.2f, 0.7f, 1f), ConstellationPoint(-5.8f, -0.9f, 1f), ConstellationPoint(-4.9f, -1f, 1f), ConstellationPoint(-0.4f, -0.5f, 1f), ConstellationPoint(0f, 0f, 1f), ConstellationPoint(3.1f, -0.3f, 1f), ConstellationPoint(4.5f, -0.5f, 1f), ConstellationPoint(5.7f, -1.8f, 1f), ConstellationPoint(7.1f, -1.5f, 2f), ConstellationPoint(6.25f, 0.6f, 1f), ConstellationPoint(8.2f, 0.45f, 1f)),
        )

        private fun darkenColor(color: Int, amount: Float): Int {
            val factor = (1f - amount).coerceIn(0f, 1f)
            return Color.argb(
                Color.alpha(color),
                (Color.red(color) * factor).toInt(),
                (Color.green(color) * factor).toInt(),
                (Color.blue(color) * factor).toInt()
            )
        }

        private fun brightenColor(color: Int, amount: Float): Int {
            val r = Color.red(color); val g = Color.green(color); val b = Color.blue(color)
            val nr = r + ((255 - r) * amount).toInt()
            val ng = g + ((255 - g) * amount).toInt()
            val nb = b + ((255 - b) * amount).toInt()
            return Color.argb(Color.alpha(color), nr.coerceIn(0, 255), ng.coerceIn(0, 255), nb.coerceIn(0, 255))
        }

        private fun lerpColor(from: Int, to: Int, t: Float): Int {
            val r = (Color.red(from) + (Color.red(to) - Color.red(from)) * t).toInt().coerceIn(0, 255)
            val g = (Color.green(from) + (Color.green(to) - Color.green(from)) * t).toInt().coerceIn(0, 255)
            val b = (Color.blue(from) + (Color.blue(to) - Color.blue(from)) * t).toInt().coerceIn(0, 255)
            val a = (Color.alpha(from) + (Color.alpha(to) - Color.alpha(from)) * t).toInt().coerceIn(0, 255)
            return Color.argb(a, r, g, b)
        }

        private fun withOpacity(color: Int, opacity: Float): Int {
            val a = (opacity.coerceIn(0f, 1f) * 255f).toInt()
            return Color.argb(a, Color.red(color), Color.green(color), Color.blue(color))
        }

        private fun easeOutExpo(t: Float): Float =
            if (t >= 1f) 1f else 1f - 2f.toDouble().pow(-10.0 * t).toFloat()

        private fun buildStarPath(into: Path, cx: Float, cy: Float, radius: Float) {
            val length = radius * 0.1f
            into.reset()
            into.moveTo(cx, cy - radius)
            into.quadTo(cx + length, cy - length, cx + radius, cy)
            into.quadTo(cx + length, cy + length, cx, cy + radius)
            into.quadTo(cx - length, cy + length, cx - radius, cy)
            into.quadTo(cx - length, cy - length, cx, cy - radius)
            into.close()
        }
    }
}
